import { useEffect, useState } from "react";
import { Plus, Shield, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { daysUntil, statusForDays, formatApiError } from "../lib/format";

function WeaponDialog({ open, initial, clients, onClose, onSaved }) {
  const [form, setForm] = useState({ client_id: "", model: "", registration: "", caliber: "", kind: "Pistola", craf_expires: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({
      client_id: initial?.client_id || clients[0]?.id || "",
      model: initial?.model || "",
      registration: initial?.registration || "",
      caliber: initial?.caliber || "",
      kind: initial?.kind || "Pistola",
      craf_expires: initial?.craf_expires || "",
      notes: initial?.notes || "",
    });
  }, [open, initial, clients]);
  if (!open) return null;
  const isEdit = !!initial?.id;
  const save = async () => {
    if (!form.client_id || !form.model.trim()) { toast.error("Selecione cliente e modelo"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/weapons/${initial.id}`, form);
      else await api.post("/weapons", form);
      toast.success(isEdit ? "Arma atualizada" : "Arma cadastrada");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" data-testid="weapon-dialog" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="close-weapon-dialog"><span aria-hidden>×</span></button>
        <span className="eyebrow">{isEdit ? "EDITAR ARMA" : "NOVA ARMA"}</span>
        <h2>Registro da arma</h2>
        <p>Vincule cada arma a um cliente com dados de CRAF, calibre e validade.</p>
        <div className="form-grid">
          <label className="span-2">Cliente
            <select data-testid="weapon-client-select" value={form.client_id} onChange={e=>setForm({...form,client_id:e.target.value})}>
              <option value="">Selecione...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Tipo
            <select data-testid="weapon-kind-select" value={form.kind} onChange={e=>setForm({...form,kind:e.target.value})}>
              {["Pistola","Revólver","Carabina","Espingarda","Rifle","Outro"].map(k=><option key={k}>{k}</option>)}
            </select>
          </label>
          <label>Calibre<input data-testid="weapon-caliber-input" value={form.caliber} onChange={e=>setForm({...form,caliber:e.target.value})} placeholder=".22 LR, 9mm, .380..."/></label>
          <label className="span-2">Modelo<input data-testid="weapon-model-input" value={form.model} onChange={e=>setForm({...form,model:e.target.value})} placeholder="Ex.: Pistola Glock G19"/></label>
          <label>Registro (SIGMA/SINARM)<input data-testid="weapon-registration-input" value={form.registration} onChange={e=>setForm({...form,registration:e.target.value})}/></label>
          <label>Validade do CRAF<input data-testid="weapon-craf-input" type="date" value={form.craf_expires || ""} onChange={e=>setForm({...form,craf_expires:e.target.value})}/></label>
          <label className="span-2">Observações<textarea data-testid="weapon-notes-input" rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></label>
        </div>
        <button data-testid="save-weapon-button" className="primary-button full" disabled={busy} onClick={save}>{busy ? "Salvando..." : (isEdit ? "Salvar alterações" : "Cadastrar arma")}</button>
      </div>
    </div>
  );
}

export default function Weapons() {
  const [weapons, setWeapons] = useState([]);
  const [clients, setClients] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = async () => {
    try {
      const [w, c] = await Promise.all([api.get("/weapons"), api.get("/clients")]);
      setWeapons(w.data); setClients(c.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Excluir esta arma e processos vinculados?")) return;
    try { await api.delete(`/weapons/${id}`); toast.success("Arma removida"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">REGISTRO PATRIMONIAL</span>
          <h1>Armas</h1>
          <p>CRAFs, calibres e prazos concentrados em um só lugar.</p>
        </div>
        <button data-testid="add-weapon-button" className="primary-button" onClick={()=>setDialog({open:true, initial:null})}>
          <Plus size={17}/> Nova arma
        </button>
      </div>
      <section className="panel full-panel">
        <div className="weapon-grid">
          {weapons.map(w => {
            const d = daysUntil(w.craf_expires);
            const s = statusForDays(d);
            const label = d === null ? "Sem CRAF" : d < 0 ? "Vencido" : d <= 30 ? "Renovar" : d <= 90 ? "Atenção" : "Regular";
            return (
              <div className="weapon-card" data-testid={`weapon-card-${w.id}`} key={w.id}>
                <div className="weapon-top">
                  <Shield size={20}/>
                  <label className={`status ${s==="urgente"?"warning-status":s==="atencao"?"attention-status":""}`}>{label}</label>
                </div>
                <h3>{w.model}</h3>
                <p>{w.client_name}</p>
                <div className="weapon-meta">
                  <span><small>REGISTRO</small>{w.registration || "—"}</span>
                  <span><small>CALIBRE</small>{w.caliber || "—"}</span>
                </div>
                <div className="weapon-footer">
                  <span>Validade CRAF</span>
                  <b>{w.craf_expires ? new Date(`${w.craf_expires}T12:00:00`).toLocaleDateString("pt-BR") : "—"}</b>
                </div>
                <div className="weapon-actions">
                  <button data-testid={`edit-weapon-${w.id}`} className="icon-mini" onClick={()=>setDialog({open:true, initial:w})}><Pencil size={14}/></button>
                  <button data-testid={`delete-weapon-${w.id}`} className="icon-mini danger" onClick={()=>remove(w.id)}><Trash2 size={14}/></button>
                </div>
              </div>
            );
          })}
          {!weapons.length && <div className="empty-inline" style={{gridColumn:"1 / -1"}}>Nenhuma arma cadastrada.</div>}
        </div>
      </section>
      <WeaponDialog open={dialog.open} initial={dialog.initial} clients={clients} onClose={()=>setDialog({open:false, initial:null})} onSaved={load}/>
    </>
  );
}
